// Fog over one player's half of the mat.
//
// Some cards (Fog, Smog: the `field_aura` cell in cards.csv) cover their owner's side of the board for as
// long as they are in play. Only the OTHER player sees it: the caster's own view of their field stays clear.
// It is see-through on purpose: a few thin sheets of slow cloud, one above the other, drifting different ways.
// The look of each kind (colors, how thick, how fast) is in src/config.ts under fieldAuras.
import * as THREE from 'three';
import { CONFIG } from '../../config.ts';

type Look = (typeof CONFIG.fieldAuras.kinds)[string];

let shared: { cloud: THREE.Texture; grime: THREE.Texture; edge: THREE.Texture } | null = null;
function textures() {
  if (shared) return shared;
  // seeded, so the fog looks the same every time and for both players
  let seed = 20240917;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const sheet = (blobs: number, least: number, most: number, alpha: number) => {
    const size = 512;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    for (let i = 0; i < blobs; i++) {
      const x = rnd() * size;
      const y = rnd() * size;
      const r = least + rnd() * (most - least);
      const a = alpha * (0.5 + rnd() * 0.5);
      // drawn again across each edge, so the sheet repeats without a seam
      for (const dx of [-size, 0, size]) {
        for (const dy of [-size, 0, size]) {
          if (x + dx + r < 0 || x + dx - r > size || y + dy + r < 0 || y + dy - r > size) continue;
          const g = ctx.createRadialGradient(x + dx, y + dy, 0, x + dx, y + dy, r);
          g.addColorStop(0, `rgba(255,255,255,${a})`);
          g.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.fillStyle = g;
          ctx.fillRect(x + dx - r, y + dy - r, r * 2, r * 2);
        }
      }
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return t;
  };
  const cloud = sheet(46, 60, 150, 0.2); // big, soft banks
  const grime = sheet(120, 14, 46, 0.28); // small, blotchy patches: the dirt in smog
  // fades the fog out toward the edges of the half it covers, so it has no hard border
  const e = document.createElement('canvas');
  e.width = e.height = 128;
  const ectx = e.getContext('2d')!;
  ectx.fillStyle = '#000';
  ectx.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 20; i++) {
    const inset = i * 1.5;
    const v = Math.round(255 * Math.min(1, (i + 1) / 16));
    ectx.fillStyle = `rgb(${v},${v},${v})`;
    ectx.beginPath();
    ectx.roundRect(inset, inset, 128 - inset * 2, 128 - inset * 2, 22);
    ectx.fill();
  }
  const edge = new THREE.CanvasTexture(e);
  shared = { cloud, grime, edge };
  return shared;
}

interface Sheet {
  mat: THREE.MeshBasicMaterial;
  map: THREE.Texture;
  opacity: number;
  driftX: number;
  driftZ: number;
}

class Bank {
  readonly group = new THREE.Group();
  private sheets: Sheet[] = [];
  shown = 0; // 0 = gone, 1 = fully there (it rolls in and out)
  wanted = false;

  constructor(look: Look, side: 1 | -1) {
    const t = textures();
    const width = CONFIG.mat.width + 3; // just past the mat's edge, where it thins out to nothing
    const depth = CONFIG.mat.depth / 2 + 1.5;
    look.sheets.forEach((s, i) => {
      const map = (s.grime ? t.grime : t.cloud).clone();
      map.needsUpdate = true;
      map.repeat.set(s.scale * (width / depth), s.scale);
      map.offset.set(i * 0.37, i * 0.61);
      const mat = new THREE.MeshBasicMaterial({
        map,
        alphaMap: t.edge,
        color: s.color,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.NormalBlending,
      });
      const geo = new THREE.PlaneGeometry(width, depth);
      geo.rotateX(-Math.PI / 2);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(0, look.height + i * 0.9, side * (depth / 2 - 0.5));
      mesh.renderOrder = 12 + i; // over the cards and their tokens, under attack arrows and pings
      this.group.add(mesh);
      this.sheets.push({ mat, map, opacity: s.opacity, driftX: s.drift[0], driftZ: s.drift[1] });
    });
    this.group.visible = false;
  }

  update(time: number, dt: number) {
    this.shown += ((this.wanted ? 1 : 0) - this.shown) * Math.min(1, dt * 1.6);
    this.group.visible = this.shown > 0.01;
    if (!this.group.visible) return;
    this.sheets.forEach((s, i) => {
      s.map.offset.set(i * 0.37 + time * s.driftX, i * 0.61 + time * s.driftZ);
      s.mat.opacity = s.opacity * this.shown * (0.9 + 0.1 * Math.sin(time * 0.5 + i * 2));
    });
  }

  dispose() {
    this.group.removeFromParent();
    for (const s of this.sheets) {
      s.map.dispose();
      s.mat.dispose();
    }
    for (const m of this.group.children) (m as THREE.Mesh).geometry.dispose();
  }
}

export class FieldFog {
  private scene: THREE.Scene;
  private banks = new Map<string, Bank>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * Which fogs should be showing right now: "fog|1" = fog over the near half of the mat (yours, in your
   * view), "smog|-1" = smog over the far half. Anything not listed rolls away.
   */
  show(wanted: Set<string>) {
    for (const key of wanted) {
      if (this.banks.has(key)) continue;
      const [kind, side] = key.split('|');
      const look = CONFIG.fieldAuras.kinds[kind];
      if (!look) continue;
      const bank = new Bank(look, side === '1' ? 1 : -1);
      this.scene.add(bank.group);
      this.banks.set(key, bank);
    }
    for (const [key, bank] of this.banks) bank.wanted = wanted.has(key);
  }

  update(time: number, dt: number) {
    for (const [key, bank] of this.banks) {
      bank.update(time, dt);
      if (!bank.wanted && bank.shown < 0.01) {
        bank.dispose();
        this.banks.delete(key);
      }
    }
  }

  dispose() {
    for (const bank of this.banks.values()) bank.dispose();
    this.banks.clear();
  }
}
